import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { ResourceRequestStatus } from '~/types';
import { useDialogState } from '~/modules/common/hooks';
import { useHasPermission } from '~/modules/common/permissions';

import { AddFabButton } from '~/modules/common/components';
import AddResourceRequestDrawer from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/AddResourceRequestDrawer';
import { ProjectChartRowsLoading } from '~/modules/resourcing/common/components';
import { useGetScheduleDurationByDayForUsers } from '~/modules/resourcing/common/hooks';
import {
  getUserIdsFromRequests,
  getUserScheduleDurationSet,
  mapScheduleDurationByDayToRequests
} from '~/modules/resourcing/common/util/resourceAllocationUtil';
import { ResourceRequestToolbar } from './components';
import ResourceRequestChart from './ResourceRequestChart';
import { useResourceRequestToolbarContext } from './hooks';

const useStyles = makeStyles(() => ({
  fab: {
    zIndex: 20
  }
}));

export const DesktopResourcingPlanPage = ({
  project,
  resourceRequests,
  isEditSwitchEnabled,
  hasMoreRows,
  loadingRows,
  loadMoreRows,
  loadingMoreRows
}) => {
  const intl = useIntl();

  const { open, closeDialog, openDialog } = useDialogState();

  const isResourceAssignmentEnabled = useHasPermission({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });

  const editProjectEnabled = useHasPermission({
    actionUri: 'urn:replicon:project-action:edit-project'
  });

  const classes = useStyles();

  const {
    scale,
    chartDates,
    dateRange: chartDisplayDateRange,
    sort,
    onSortChange
  } = useResourceRequestToolbarContext();

  const showAssigneeRow =
    isResourceAssignmentEnabled &&
    resourceRequests.filter(
      r => r.requestStatus !== ResourceRequestStatus.Draft
    ).length !== 0;

  const resourceAllocationUserIds = useMemo(
    () => getUserIdsFromRequests(resourceRequests),
    [resourceRequests]
  );

  const chartDateRange = useMemo(
    () => ({
      startDate: chartDates[0].start,
      endDate: chartDates[chartDates.length - 1].end
    }),
    [chartDates[0].start, chartDates[chartDates.length - 1].end]
  );

  const {
    scheduleDurations = [],
    loadingRows: scheduleLoading
  } = useGetScheduleDurationByDayForUsers({
    userIds: resourceAllocationUserIds,
    dateRange: chartDateRange
  });

  const resourceRequestsWithScheduleDurations = useMemo(
    () =>
      mapScheduleDurationByDayToRequests(
        getUserScheduleDurationSet(scheduleDurations),
        resourceRequests
      ),
    [resourceRequests, scheduleDurations]
  );

  if (scheduleLoading) {
    return <ProjectChartRowsLoading />;
  }

  return (
    <>
      <ResourceRequestToolbar />
      <ResourceRequestChart
        scale={scale}
        resourceRequests={resourceRequestsWithScheduleDurations}
        chartDates={chartDates}
        isEditSwitchEnabled={isEditSwitchEnabled}
        showAssigneeRow={showAssigneeRow}
        chartDisplayDateRange={chartDisplayDateRange}
        handleAddResourceRequestClick={openDialog}
        sort={sort}
        onSortChange={onSortChange}
        hasMoreRows={hasMoreRows}
        loadingRows={loadingRows}
        loadMoreRows={loadMoreRows}
        loadingMoreRows={loadingMoreRows}
      />
      {editProjectEnabled && isEditSwitchEnabled && (
        <AddFabButton
          className={classes.fab}
          label={intl.formatMessage({ id: 'addResourceRequestButton.label' })}
          shortLabel={intl.formatMessage({
            id: 'addResourceRequestButton.shortLabel'
          })}
          dataQeId="addResourceRequestOpenDialog"
          onClick={openDialog}
        />
      )}
      {open && (
        <AddResourceRequestDrawer
          project={project}
          onClose={closeDialog}
          open={open}
        />
      )}
    </>
  );
};

DesktopResourcingPlanPage.propTypes = {
  project: PropTypes.object.isRequired,
  resourceRequests: PropTypes.array.isRequired,
  isEditSwitchEnabled: PropTypes.bool.isRequired,
  filter: PropTypes.object,
  hasMoreRows: PropTypes.bool.isRequired,
  loadMoreRows: PropTypes.func.isRequired,
  loadingMoreRows: PropTypes.bool.isRequired,
  loadingRows: PropTypes.bool.isRequired
};

export default DesktopResourcingPlanPage;
